import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import classNames from '../../../utils/classNames';
import CoverArt, { pickCoverVariant } from '../../atoms/CoverArt';
import {
    mapViewerStatusToVariant,
    resolveViewerStatusLabel,
    formatShortScheduledLabel,
} from '../../../utils/eventPresentation';
import './index.scss';

const VARIANT_TO_COLOR = {
    going: 'var(--s-going-fg)',
    interested: 'var(--s-interest-fg)',
    pending: 'var(--s-pending-fg)',
    cancel: 'var(--s-cancel-fg)',
    decline: 'var(--s-decline-fg)',
    publish: 'var(--s-publish-fg)',
    complete: 'var(--s-complete-fg)',
};

export default function CalendarThisWeekList({
    events = [],
    onOpen,
    eyebrowLabel,
    emptyLabel,
    className = '',
}) {
    const { t, i18n } = useTranslation();
    const locale = i18n.resolvedLanguage || i18n.language || 'en-US';

    return (
        <section className={classNames('calendar-this-week', className)}>
            <h4 className="calendar-this-week__eyebrow">
                {eyebrowLabel || t('eventsPage.calendar.sideRail.thisWeek')}
            </h4>
            {events.length === 0 ? (
                <p className="calendar-this-week__empty">
                    {emptyLabel || t('eventsPage.calendar.empty.week')}
                </p>
            ) : (
                <ul className="calendar-this-week__list">
                    {events.map((event) => {
                        const variant = event?.viewerAttendanceStatus
                            ? mapViewerStatusToVariant(event.viewerAttendanceStatus)
                            : 'publish';
                        const viewerLabel = resolveViewerStatusLabel(t, event?.viewerAttendanceStatus);
                        const scheduledLabel = formatShortScheduledLabel(locale, event?.startAt, event?.timezone);
                        const coverVariant = pickCoverVariant(event?.id || event?.title);

                        return (
                            <li key={event.id}>
                                <button
                                    type="button"
                                    className="calendar-this-week__row"
                                    onClick={() => {
                                        if (typeof onOpen === 'function') {
                                            onOpen(event);
                                        }
                                    }}
                                >
                                    <CoverArt
                                        className="calendar-this-week__thumb"
                                        variant={coverVariant}
                                    />
                                    <div className="calendar-this-week__copy">
                                        <div className="calendar-this-week__name">{event.title}</div>
                                        <div className="calendar-this-week__meta">
                                            {scheduledLabel}
                                            {viewerLabel ? ` · ${viewerLabel}` : ''}
                                        </div>
                                    </div>
                                    <span
                                        className="calendar-this-week__tag"
                                        style={{ color: VARIANT_TO_COLOR[variant] || VARIANT_TO_COLOR.publish }}
                                    >
                                        • {viewerLabel || t(`home.postCard.relatedObject.status.${(event?.status || 'published').toLowerCase()}`)}
                                    </span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
}

CalendarThisWeekList.propTypes = {
    events: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string,
        startAt: PropTypes.string,
        timezone: PropTypes.string,
        status: PropTypes.string,
        viewerAttendanceStatus: PropTypes.string,
    })),
    onOpen: PropTypes.func,
    eyebrowLabel: PropTypes.string,
    emptyLabel: PropTypes.string,
    className: PropTypes.string,
};
